/**
 * Hook to detect if the device supports touch input
 *
 * This hook detects touch capability to conditionally show/hide
 * UI elements that are redundant on touch devices (like zoom buttons
 * when pinch-to-zoom is available).
 */

import { useState, useEffect } from 'react';

/**
 * Detects if the current device has touch capability
 * @returns {boolean} true if device supports touch, false otherwise
 */
export function useTouchDevice(): boolean {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    // Check for touch capability
    const checkTouchDevice = () => {
      // Multiple checks for maximum compatibility
      const hasTouch = (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-ignore - for older browsers
        navigator.msMaxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches
      );

      setIsTouchDevice(hasTouch);
    };

    // Initial check
    checkTouchDevice();

    // Also listen for changes (e.g., connecting/disconnecting touch devices)
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    const handleChange = () => checkTouchDevice();

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Legacy browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return isTouchDevice;
}